import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarEmpreendimentosFluxoCaixa } from '~/Services/Service';

const OPCOES_AGRUPAMENTO = [
  { value: 'Mensal', label: 'Mensal' },
  { value: 'Diario', label: 'Diário' },
];

const OPCOES_SINAL_COMISSAO = [
  { value: 0, label: 'Todos' },
  { value: 1, label: 'Apenas Imob.' },
  { value: 2, label: 'Exceto Imob.' },
];

const OPCOES_SIM_NAO = [
  { value: true, label: 'Sim' },
  { value: false, label: 'Não' },
];

const Filtros = ({ values, errors, touched, setFieldValue, handleChange, handleBlur, grupos }) => {
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [carregandoEmp, setCarregandoEmp] = useState(false);

  const periodoDias = useMemo(() => {
    if (!values.dataInicial || !values.dataFinal) return 0;
    const ini = moment(values.dataInicial, 'YYYY-MM-DD', true);
    const fim = moment(values.dataFinal, 'YYYY-MM-DD', true);
    if (!ini.isValid() || !fim.isValid()) return 0;
    return fim.diff(ini, 'days');
  }, [values.dataInicial, values.dataFinal]);

  const bloqueadoDiario = periodoDias >= 1825;

  useEffect(() => {
    if (bloqueadoDiario && values.agrupamento === 'Diario') {
      setFieldValue('agrupamento', 'Mensal');
    }
  }, [bloqueadoDiario]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let ativo = true;

    const carregarEmpreendimentos = async () => {
      if (!values.codigosGrupoEmpresa || values.codigosGrupoEmpresa.length === 0) {
        setEmpreendimentos([]);
        setFieldValue('codigoFamiliaEmpreendimento', null);
        return;
      }

      setCarregandoEmp(true);
      try {
        const res = await listarEmpreendimentosFluxoCaixa(values.codigosGrupoEmpresa);
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        setEmpreendimentos(lista);
      } catch (err) {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar empreendimentos.';
        toast.error(msg);
      } finally {
        if (ativo) setCarregandoEmp(false);
      }
    };

    carregarEmpreendimentos();

    return () => { ativo = false; };
  }, [values.codigosGrupoEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

  const opcoesGE = useMemo(
    () => grupos.map((g) => ({ value: g.codigo_grupo_empresa, label: g.apelido })),
    [grupos]
  );

  const opcoesEmp = useMemo(
    () => [
      { value: null, label: 'Todos os empreendimentos' },
      ...empreendimentos.map((e) => ({ value: e.codigo_familia, label: e.nome })),
    ],
    [empreendimentos]
  );

  const gesSelecionados = useMemo(
    () => opcoesGE.filter((o) => values.codigosGrupoEmpresa.includes(o.value)),
    [opcoesGE, values.codigosGrupoEmpresa]
  );

  const empSelecionado = useMemo(
    () => opcoesEmp.find((o) => o.value === values.codigoFamiliaEmpreendimento) || opcoesEmp[0] || null,
    [opcoesEmp, values.codigoFamiliaEmpreendimento]
  );

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>Filtros</strong>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Data Inicial</Form.Label>
              <Form.Control
                type="date"
                name="dataInicial"
                value={values.dataInicial}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.dataInicial && !!errors.dataInicial}
              />
              {touched.dataInicial && errors.dataInicial && (
                <Form.Control.Feedback type="invalid">
                  {errors.dataInicial}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Data Final</Form.Label>
              <Form.Control
                type="date"
                name="dataFinal"
                value={values.dataFinal}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.dataFinal && !!errors.dataFinal}
              />
              {touched.dataFinal && errors.dataFinal && (
                <Form.Control.Feedback type="invalid">
                  {errors.dataFinal}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Moeda</Form.Label>
              <Form.Control
                type="number"
                name="codigoMoeda"
                value={values.codigoMoeda}
                onChange={handleChange}
                onBlur={handleBlur}
                min={1}
                isInvalid={touched.codigoMoeda && !!errors.codigoMoeda}
              />
              {touched.codigoMoeda && errors.codigoMoeda && (
                <Form.Control.Feedback type="invalid">
                  {errors.codigoMoeda}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>D+ (dias)</Form.Label>
              <Form.Control
                type="number"
                name="diasD"
                value={values.diasD}
                onChange={handleChange}
                onBlur={handleBlur}
                min={0}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6} lg={4}>
            <Form.Group>
              <Form.Label>
                Agrupamento
                {bloqueadoDiario && (
                  <span className="text-warning ml-2" style={{ fontSize: '0.8rem' }}>
                    (Período ≥ 5 anos — Diário bloqueado)
                  </span>
                )}
              </Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_AGRUPAMENTO.map((o) =>
                  o.value === 'Diario' && bloqueadoDiario ? { ...o, isDisabled: true } : o
                )}
                value={OPCOES_AGRUPAMENTO.find((o) => o.value === values.agrupamento) || null}
                onChange={(opt) => setFieldValue('agrupamento', opt ? opt.value : 'Mensal')}
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={4}>
            <Form.Group>
              <Form.Label>Sinal Comissão</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_SINAL_COMISSAO}
                value={OPCOES_SINAL_COMISSAO.find((o) => o.value === values.sinalComissao) || null}
                onChange={(opt) => setFieldValue('sinalComissao', opt ? opt.value : 0)}
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={4}>
            <Form.Group>
              <Form.Label>Considera Intermediárias</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_SIM_NAO}
                value={OPCOES_SIM_NAO.find((o) => o.value === values.consideraIntermediarias) || null}
                onChange={(opt) =>
                  setFieldValue('consideraIntermediarias', opt ? opt.value : false)
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={4} lg={4}>
            <Form.Group>
              <Form.Label>Desc. Taxa Adm.</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_SIM_NAO}
                value={OPCOES_SIM_NAO.find((o) => o.value === values.taxaAdministracao) || null}
                onChange={(opt) =>
                  setFieldValue('taxaAdministracao', opt ? opt.value : true)
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={4} lg={4}>
            <Form.Group>
              <Form.Label>Desc. Impostos</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_SIM_NAO}
                value={OPCOES_SIM_NAO.find((o) => o.value === values.impostos) || null}
                onChange={(opt) =>
                  setFieldValue('impostos', opt ? opt.value : true)
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={4} lg={4}>
            <Form.Group>
              <Form.Label>Desc. Inadimplência</Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={OPCOES_SIM_NAO}
                value={OPCOES_SIM_NAO.find((o) => o.value === values.inadimplencia) || null}
                onChange={(opt) =>
                  setFieldValue('inadimplencia', opt ? opt.value : false)
                }
                isClearable={false}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>
                Grupos de Empresa{' '}
                <span className="text-danger">*</span>
              </Form.Label>
              <Select
                isMulti
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={opcoesGE}
                value={gesSelecionados}
                onChange={(opts) =>
                  setFieldValue(
                    'codigosGrupoEmpresa',
                    opts ? opts.map((o) => o.value) : []
                  )
                }
                placeholder="Selecione um ou mais GEs..."
                noOptionsMessage={() => 'Nenhum GE disponível'}
              />
              {touched.codigosGrupoEmpresa && errors.codigosGrupoEmpresa && (
                <div className="text-danger small mt-1">{errors.codigosGrupoEmpresa}</div>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>
                Empreendimento
                {carregandoEmp && <Spinner animation="border" size="sm" className="ml-2" />}
              </Form.Label>
              <Select
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={opcoesEmp}
                value={empSelecionado}
                onChange={(opt) =>
                  setFieldValue('codigoFamiliaEmpreendimento', opt ? opt.value : null)
                }
                isDisabled={values.codigosGrupoEmpresa.length === 0 || carregandoEmp}
                placeholder={carregandoEmp ? 'Carregando...' : 'Todos'}
              />
            </Form.Group>
          </Col>
        </Row>

        {bloqueadoDiario && (
          <Alert variant="warning" className="py-2 small mb-0">
            Período maior que 5 anos — agrupamento Diário não disponível. Usando Mensal.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default Filtros;
